import { client } from '../api/client.js';

export class ClientViewPage {
    async mount(el) {
        this.el = el;
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const token = params.get('token');

        if (!token) {
            el.innerHTML = this.renderError('Invalid Link', 'This share link is missing a token.');
            return;
        }

        document.body.classList.add('client-view-mode');
        el.innerHTML = this.renderLoading();

        try {
            const data = await client.getClientView(token);
            if (!data.ok) throw new Error(data.error || 'Failed to load');
            this.token = token;
            this.data = data;
            this.renderView(el);
        } catch (err) {
            el.innerHTML = this.renderError('Link Unavailable', err.message || 'This share link may have expired or been revoked.');
        }
    }

    unmount() {
        document.body.classList.remove('client-view-mode');
    }

    renderLoading() {
        return '<div class="cv-container"><div class="cv-loading">Loading client view...</div></div>';
    }

    renderError(title, message) {
        const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        return `
            <div class="cv-container">
                <div class="cv-error">
                    <h2>${_esc(title)}</h2>
                    <p>${_esc(message)}</p>
                    <a href="https://heistbrain.com" style="color:#8B5CF6;margin-top:16px;display:inline-block;">Visit Heist</a>
                </div>
            </div>
        `;
    }

    renderView(el) {
        const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        const { brand, calendar, content } = this.data;

        el.innerHTML = `
            <div class="cv-container">
                <header class="cv-header">
                    <h1 class="cv-brand-name">${_esc(brand.name)}</h1>
                    <p class="cv-subtitle">Content preview — shared via Heist</p>
                </header>

                <section class="cv-section">
                    <h2 class="cv-section-title">Upcoming Schedule</h2>
                    ${calendar.length > 0 ? `
                        <div class="cv-calendar-list">
                            ${calendar.map(c => `
                                <div class="cv-calendar-item">
                                    <div class="cv-calendar-date">${new Date(c.scheduledFor).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                                    <div class="cv-calendar-platform">${_esc(c.platform)}</div>
                                    <div class="cv-calendar-content">${_esc(c.content || '')}</div>
                                </div>
                            `).join('')}
                        </div>
                    ` : '<p class="cv-empty">No upcoming posts scheduled.</p>'}
                </section>

                <section class="cv-section">
                    <h2 class="cv-section-title">Recent Content</h2>
                    ${content.length > 0 ? `
                        <div class="cv-content-list">
                            ${content.map(c => `
                                <div class="cv-content-item" data-content-id="${c.id}">
                                    <div class="cv-content-header">
                                        <span class="cv-content-platform">${_esc(c.platform)}</span>
                                        <span class="cv-content-status cv-content-status--${c.status}">${c.status.replace('_', ' ')}</span>
                                    </div>
                                    ${c.title ? `<div class="cv-content-title">${_esc(c.title)}</div>` : ''}
                                    <div class="cv-content-preview">${_esc(c.content || '')}</div>
                                    <button class="cv-feedback-btn" data-action="feedback" data-content-id="${c.id}">Leave Feedback</button>
                                </div>
                            `).join('')}
                        </div>
                    ` : '<p class="cv-empty">No content to review.</p>'}
                </section>

                <section class="cv-section cv-feedback-section" id="cv-feedback-form" style="display:none;">
                    <h2 class="cv-section-title">Your Feedback</h2>
                    <input class="cv-input" id="cv-author" placeholder="Your name *" required>
                    <input class="cv-input" id="cv-email" placeholder="Your email (optional)" type="email">
                    <textarea class="cv-textarea" id="cv-comment" placeholder="Your feedback..." rows="4" required></textarea>
                    <input type="hidden" id="cv-content-id" value="">
                    <div class="cv-feedback-actions">
                        <button class="cv-btn-primary" data-action="submit-feedback">Submit Feedback</button>
                        <button class="cv-btn-ghost" data-action="cancel-feedback">Cancel</button>
                    </div>
                    <div id="cv-feedback-msg" style="display:none;margin-top:12px;"></div>
                </section>

                <footer class="cv-footer">
                    <p>Powered by <a href="https://heistbrain.com" target="_blank">Heist</a> — The brains behind the operation.</p>
                </footer>
            </div>
        `;

        el.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn) return;

            if (btn.dataset.action === 'feedback') {
                const form = el.querySelector('#cv-feedback-form');
                form.style.display = 'block';
                el.querySelector('#cv-content-id').value = btn.dataset.contentId || '';
                form.scrollIntoView({ behavior: 'smooth' });
            }

            if (btn.dataset.action === 'cancel-feedback') {
                el.querySelector('#cv-feedback-form').style.display = 'none';
            }

            if (btn.dataset.action === 'submit-feedback') {
                const name = el.querySelector('#cv-author')?.value?.trim();
                const email = el.querySelector('#cv-email')?.value?.trim();
                const comment = el.querySelector('#cv-comment')?.value?.trim();
                const contentId = el.querySelector('#cv-content-id')?.value;
                const msg = el.querySelector('#cv-feedback-msg');

                if (!name || !comment) {
                    msg.textContent = 'Name and feedback are required.';
                    msg.style.display = 'block';
                    msg.style.color = 'var(--danger, #ef4444)';
                    return;
                }

                btn.disabled = true;
                btn.textContent = 'Submitting...';
                try {
                    await client.submitClientFeedback(this.token, {
                        author_name: name, author_email: email || null,
                        comment, content_item_id: contentId || null
                    });
                    msg.textContent = 'Thank you! Your feedback has been submitted.';
                    msg.style.display = 'block';
                    msg.style.color = 'var(--success, #22c55e)';
                    el.querySelector('#cv-comment').value = '';
                } catch (err) {
                    msg.textContent = err.message || 'Failed to submit feedback.';
                    msg.style.display = 'block';
                    msg.style.color = 'var(--danger, #ef4444)';
                } finally {
                    btn.disabled = false;
                    btn.textContent = 'Submit Feedback';
                }
            }
        });
    }
}
